import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Trophy, Lock, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { fmt, gbErr } from './gbAdminUtils';

const STATUS = {
  AWAITING_APPROVAL: { t: 'aguardando sua aprovação', c: 'text-amber-600' },
  PROCESSING: { t: 'em processamento na KAKO', c: 'text-blue-600' },
  FAILED: { t: 'falhou — pode tentar de novo', c: 'text-rose-600' },
  BLOCKED: { t: 'retido (contestação em aberto)', c: 'text-rose-700' },
  PAID: { t: 'creditado', c: 'text-emerald-600' },
};

export default function GbJackpot({ seasonId, seasonStatus }) {
  const [rel, setRel] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/gb/season/${seasonId}/jackpot`);
      setRel(data);
    } catch (e) { toast.error(gbErr(e)); }
  }, [seasonId]);

  useEffect(() => { load(); }, [load]);

  const acao = async (url, msg, key) => {
    setBusy(key);
    try { await api.post(url); toast.success(msg); await load(); }
    catch (e) { toast.error(gbErr(e)); } finally { setBusy(''); }
  };

  if (!rel) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  const pote = rel.pot || {};
  const salvos = rel.payouts || [];

  return (
    <div className="space-y-4" data-testid="gb-admin-jackpot">
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border p-3" data-testid="gb-jackpot-pot">
          <p className="text-[10px] uppercase text-slate-400">jackpot acumulado</p>
          <p className="text-base font-black text-amber-600">{fmt(pote.jackpot || 0)} moedas</p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">parte da banca</p>
          <p className="text-base font-black text-slate-700">{fmt(pote.house || 0)} moedas</p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">duelos disputados</p>
          <p className="text-base font-black text-slate-700">{fmt(pote.matches || 0)}</p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">já creditado</p>
          <p className="text-base font-black text-emerald-600">{fmt(rel.paid_coins || 0)} moedas</p>
        </div>
      </div>

      <div className="rounded-xl border bg-slate-50 p-3 text-xs text-slate-600">
        O jackpot é dividido entre o <b>top 3 do ranking de PvP</b>: 50% / 30% / 20%. O servidor só
        calcula e congela os ganhadores — <b>nada é creditado sem a sua aprovação</b>.
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <h4 className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
            <Trophy className="h-4 w-4 text-amber-500" />Top 3 do PvP agora
          </h4>
          <Button size="sm" variant="outline" onClick={load} data-testid="gb-jackpot-reload">
            <RefreshCw className="mr-1 h-3.5 w-3.5" />atualizar
          </Button>
          <Button size="sm" onClick={() => acao(`/admin/gb/season/${seasonId}/jackpot/freeze`,
            'Ganhadores congelados aguardando aprovação.', 'freeze')}
            disabled={busy === 'freeze'} data-testid="gb-jackpot-freeze">
            {busy === 'freeze' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Lock className="mr-1 h-3.5 w-3.5" />}
            CONGELAR GANHADORES
          </Button>
        </div>
        {(rel.winners || []).length === 0 && (
          <p className="text-xs text-slate-400" data-testid="gb-jackpot-empty">
            Ninguém ganhou duelo de PvP nesta temporada ainda.
          </p>
        )}
        <div className="grid gap-2 md:grid-cols-3">
          {(rel.winners || []).map((w) => (
            <div key={w.rank} className="rounded-xl border p-3" data-testid={`gb-jackpot-top-${w.rank}`}>
              <p className="text-[10px] uppercase text-slate-400">{w.rank}º lugar</p>
              <p className="truncate text-sm font-bold text-slate-800">{w.nickname}</p>
              <p className="text-[11px] text-slate-500">KAKO {w.kako_id} · {w.pvp_wins} vitórias</p>
              <p className="mt-1 text-base font-black text-amber-600">{fmt(w.coins)} moedas</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <h4 className="text-sm font-bold text-slate-700">Prêmios congelados</h4>
          {salvos.some((r) => r.status !== 'PAID') && (
            <Button size="sm" onClick={() => acao(`/admin/gb/season/${seasonId}/jackpot/approve-all`,
              'Prêmios liberados para crédito.', 'all')}
              disabled={busy === 'all'} data-testid="gb-jackpot-approve-all">
              {busy === 'all' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
              APROVAR E CREDITAR TODOS
            </Button>
          )}
        </div>
        {salvos.length === 0 && (
          <p className="text-xs text-slate-400" data-testid="gb-jackpot-none">
            Nada congelado. Ao fechar a temporada os ganhadores entram aqui automaticamente
            {seasonStatus === 'active' ? ' — ou use "congelar ganhadores" antes.' : '.'}
          </p>
        )}
        <div className="space-y-2">
          {salvos.map((r) => {
            const st = STATUS[r.status] || STATUS.AWAITING_APPROVAL;
            return (
              <div key={r.reference} data-testid={`gb-jackpot-payout-${r.rank}`}
                className="flex flex-wrap items-center gap-3 rounded-xl border p-3">
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-black text-amber-700">{r.rank}º</span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-800">{r.nickname}</p>
                  <p className="text-[11px] text-slate-500">KAKO {r.kako_id} · ref {r.reference}</p>
                </div>
                <p className="text-sm font-black text-slate-800">{fmt(r.coins)} moedas</p>
                <span className={`flex items-center gap-1 text-[11px] font-bold ${st.c}`}>
                  {r.status === 'PAID' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                  {st.t}
                </span>
                {r.message && <span className="text-[11px] text-slate-400">{r.message}</span>}
                {r.status !== 'PAID' && r.status !== 'PROCESSING' && (
                  <Button size="sm" className="ml-auto"
                    onClick={() => acao(`/admin/gb/season/${seasonId}/jackpot/${r.rank}/approve`,
                      'Prêmio processado.', `r${r.rank}`)}
                    disabled={busy === `r${r.rank}`} data-testid={`gb-jackpot-approve-${r.rank}`}>
                    {busy === `r${r.rank}` ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                    APROVAR E CREDITAR
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
